import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import InputError from '@/components/layout/InputError';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Combobox } from '@/components/ui/shadcn/combobox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { useSettings } from '@/contexts/SettingsContext';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useCompanyCategoryOptions, useCompanyTypeOptions } from '@/hooks/useTranslatedSettingOptions';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { createUpdateCompanyValidationSchema } from '@/lib/validations/company-validation';
import type { CompanyFormData, EditCompanyProps } from '@/types/forms/companies';
import { router, useForm } from '@inertiajs/react';
import { Building2, Edit, Info, Lock, Mail, MapPin, Phone } from 'lucide-react';
import { useEffect, useMemo, type FormEvent } from 'react';
import { toast } from 'sonner';

const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';

type CompanyAddressFields = {
    address_lat?: number | null;
    address_lng?: number | null;
    address_street?: string | null;
    address_house_number?: string | null;
    address_postal_code?: string | null;
    address_city?: string | null;
    address_municipality?: string | null;
    address_country?: string | null;
};

/**
 * Compose the display address from the parts that are actually stored.
 *
 * There is no `address` column — the backend drops the field before saving and
 * never sends one back, so seeding the form from `company.address` alone left it
 * permanently empty while the input still *looked* filled (it falls back to the
 * parts for display). Composing here keeps what is shown and what is submitted
 * the same value.
 */
function composeAddress(company: CompanyAddressFields & { address?: string | null }): string {
    if (company.address) {
        return company.address;
    }

    return [company.address_street, company.address_house_number, company.address_city].filter(Boolean).join(' ').trim();
}

export default function EditCompany({ company, trigger, open: openProp, onOpenChange }: EditCompanyProps) {
    const companyAddress = company as CompanyAddressFields;
    const { legacy, settings } = useSettings();
    const { t } = useTranslations();
    const companyTypeOptions = useCompanyTypeOptions();
    const companyCategoryOptions = useCompanyCategoryOptions();

    const validationSchema = useMemo(() => {
        if (!(settings.countries?.length > 0)) {
            return null;
        }
        return createUpdateCompanyValidationSchema({
            countries: settings.countries.map((country: { value: string }) => country.value),
            companyCategories: settings.companyCategories,
            companyTypes: settings.companyTypes,
            companyPriorities: settings.companyPriorities,
            messages: {
                name_required: t('companies.forms.add.validation.name_required', 'Company name is required.'),
                name_max: t('companies.forms.add.validation.name_max', 'Company name cannot exceed 255 characters.'),
                company_code_required: t('companies.forms.add.validation.company_code_required', 'Company code is required.'),
                company_type_required: t('companies.forms.edit.validation.company_type_required', 'Company type is required.'),
                country_required: t('companies.forms.edit.validation.country_required', 'Country is required.'),
                category_invalid: t('companies.forms.edit.validation.category_invalid', 'Selected category is invalid.'),
                phone_invalid: t('companies.forms.edit.validation.phone_invalid', 'Please enter a valid phone number.'),
                email_invalid: t('companies.forms.edit.validation.email_invalid', 'Please enter a valid email address.'),
            },
        });
    }, [settings.countries, settings.companyCategories, settings.companyTypes, settings.companyPriorities, t]);

    const { data, setData, patch, errors, setError, clearErrors, processing } = useForm<CompanyFormData>({
        name: company.name,
        category: Array.isArray(company.category) ? company.category : [],
        company_code: company.company_code,
        vat: company.vat || '',
        country: company.country,
        address: composeAddress(company),
        address_lat: companyAddress.address_lat ?? null,
        address_lng: companyAddress.address_lng ?? null,
        address_street: companyAddress.address_street ?? null,
        address_house_number: companyAddress.address_house_number ?? null,
        address_postal_code: companyAddress.address_postal_code ?? null,
        address_city: companyAddress.address_city ?? null,
        address_municipality: companyAddress.address_municipality ?? null,
        address_country: companyAddress.address_country ?? null,
        domain: company.domain || '',
        phone: company.phone || '',
        email: company.email || '',
        type: company.type || '',
    });

    const [isOpen, setIsOpen] = useControllableOpen(openProp, onOpenChange);

    const requiredMark = <span className="text-destructive">*</span>;

    const getCountryValue = (): string => {
        if (!data.country) return '';
        if (typeof data.country === 'string') return data.country;
        if (typeof data.country === 'object' && data.country !== null && 'value' in data.country) {
            return String((data.country as { value: unknown }).value);
        }
        return '';
    };

    const getTypeValue = (): string => {
        if (!data.type) return '';
        if (typeof data.type === 'string') return data.type;
        if (typeof data.type === 'object' && data.type !== null && 'value' in data.type) {
            return String((data.type as { value: unknown }).value);
        }
        return '';
    };

    const countryLabel = useMemo(() => {
        const value = getCountryValue();
        if (!value) {
            return t('companies.forms.add.country_placeholder', '—');
        }
        const match = Array.isArray(legacy.COUNTRY_OPTIONS)
            ? legacy.COUNTRY_OPTIONS.find((option) => String(option.value).toUpperCase() === value.toUpperCase())
            : undefined;
        return match ? String(match.label) : value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.country, legacy.COUNTRY_OPTIONS, t]);

    const resolveCountryFromAddress = (countryCode?: string, countryName?: string): string | null => {
        const normalizedCode = countryCode?.trim().toUpperCase();
        const normalizedName = countryName?.trim().toLowerCase();

        if (Array.isArray(legacy.COUNTRY_OPTIONS)) {
            if (normalizedCode) {
                const byCode = legacy.COUNTRY_OPTIONS.find((option) => String(option.value).toUpperCase() === normalizedCode);
                if (byCode) return String(byCode.value);
            }
            if (normalizedName) {
                const byName = legacy.COUNTRY_OPTIONS.find((option) => String(option.label).toLowerCase() === normalizedName);
                if (byName) return String(byName.value);
            }
        }

        if (normalizedCode) return normalizedCode;
        return null;
    };

    // Re-seed from the server only when the dialog is opened.
    //
    // This used to depend on the `company` prop object, which gets a fresh
    // identity on every Inertia response — including the one carrying validation
    // errors. That re-ran the effect mid-edit and reset every field to the saved
    // values, silently discarding whatever the user had just picked (a newly
    // chosen category would vanish the moment the save failed).
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setData('name', company.name);
        setData('category', Array.isArray(company.category) ? company.category : []);
        setData('company_code', company.company_code);
        setData('vat', company.vat || '');
        setData('country', company.country);
        setData('address', composeAddress(company));
        setData('address_lat', companyAddress.address_lat ?? null);
        setData('address_lng', companyAddress.address_lng ?? null);
        setData('address_street', companyAddress.address_street ?? null);
        setData('address_house_number', companyAddress.address_house_number ?? null);
        setData('address_postal_code', companyAddress.address_postal_code ?? null);
        setData('address_city', companyAddress.address_city ?? null);
        setData('address_municipality', companyAddress.address_municipality ?? null);
        setData('address_country', companyAddress.address_country ?? null);
        setData('domain', company.domain || '');
        setData('phone', company.phone || '');
        setData('email', company.email || '');
        setData('type', company.type || '');
        // Deliberately keyed on the dialog opening and the company identity, not
        // on the prop object — see above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, company.id]);

    const submit = (e: FormEvent) => {
        e.preventDefault();
        clearErrors();

        if (validationSchema) {
            try {
                const result = validationSchema.safeParse(data);
                if (!result.success) {
                    const seen = new Set<string>();
                    result.error.issues.forEach((err) => {
                        const field = err.path?.[0] !== undefined ? String(err.path[0]) : '';
                        if (!field || seen.has(field) || field === 'name' || field === 'company_code') return;
                        seen.add(field);
                        setError(field as never, err.message);
                    });

                    if (seen.size > 0) {
                        toast.error(t('companies.forms.edit.validation.resolve_errors', 'Please fill in all required fields.'));
                        return;
                    }
                }
            } catch (error: unknown) {
                console.error('Validation error:', error);
            }
        }

        const getAddressField = (value: string | null | undefined): string | null => {
            if (value === null || value === undefined) return null;
            const str = String(value).trim();
            return str.length > 0 ? str : null;
        };

        setData('address_lat', data.address_lat !== null && data.address_lat !== undefined ? data.address_lat : null);
        setData('address_lng', data.address_lng !== null && data.address_lng !== undefined ? data.address_lng : null);
        setData('address_street', getAddressField(data.address_street));
        setData('address_house_number', getAddressField(data.address_house_number));
        setData('address_postal_code', getAddressField(data.address_postal_code));
        setData('address_city', getAddressField(data.address_city));
        setData('address_municipality', getAddressField(data.address_municipality));
        setData('address_country', getAddressField(data.address_country as string | null));

        patch(route('companies.update', company.id), {
            onSuccess: () => {
                setIsOpen(false);
                router.reload({ only: ['companies.show'] });
            },
            onError: () => {
                // Flash message will be shown by useFlashToasts hook if backend provides one
            },
        });
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : openProp === undefined ? (
                // Only when the modal owns its open state: a page driving it
                // supplies its own buttons and must not get a stray one here.
                <ResponsiveModalTrigger asChild>
                    <Button variant={'default'}>
                        <Edit className="mr-2 h-4 w-4" /> {t('companies.forms.edit.buttons.edit_company', 'Edit company')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('companies.forms.edit.title', "Let's edit :name.").replace(':name', company.name)}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'companies.forms.edit.description',
                            'This action will edit the company details. You will not be able to change company code or name, for that - contact administrator.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-6">
                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        1
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle className="flex items-center gap-2">
                                            <Building2 className="h-4 w-4" />
                                            {t('companies.forms.add.sections_long.identification', 'Company identification')}
                                        </CardTitle>
                                        <CardDescription>
                                            {t('companies.forms.add.sections_long.identification_desc', 'Required fields are marked with *.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="flex items-center gap-1.5">
                                        {t('companies.forms.edit.labels.company', 'Company')}
                                        <Lock className="text-muted-foreground h-3 w-3" />
                                    </Label>
                                    <Input id="name" value={data.name} disabled readOnly className="bg-muted/40" autoComplete="off" />
                                </div>

                                <div className="grid items-start gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="type">
                                            {t('companies.forms.edit.labels.type', 'Type')} {requiredMark}
                                        </Label>
                                        <Combobox
                                            options={companyTypeOptions}
                                            value={getTypeValue()}
                                            onValueChange={(value) => {
                                                clearErrors('type');
                                                setData('type', value);
                                            }}
                                            placeholder={t('companies.forms.edit.placeholders.select_company_type', 'Select company type...')}
                                            searchPlaceholder={t('companies.forms.edit.placeholders.search_company_type', 'Search company type...')}
                                            emptyText={t('companies.forms.edit.placeholders.no_company_type', 'No company type found.')}
                                            className={cn(errors.type && invalidFieldClassName)}
                                        />
                                        <InputError message={errors.type} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="company_code" className="flex items-center gap-1.5">
                                            {t('companies.forms.edit.labels.code', 'Code')}
                                            <Lock className="text-muted-foreground h-3 w-3" />
                                        </Label>
                                        <Input
                                            id="company_code"
                                            value={data.company_code}
                                            disabled
                                            readOnly
                                            className="bg-muted/40"
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className="grid items-start gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="vat">{t('companies.forms.edit.labels.vat', 'VAT')}</Label>
                                        <Input
                                            id="vat"
                                            value={data.vat}
                                            aria-invalid={!!errors.vat}
                                            className={cn(errors.vat && invalidFieldClassName)}
                                            onChange={(e) => {
                                                clearErrors('vat');
                                                setData('vat', e.target.value);
                                            }}
                                            autoComplete="off"
                                            placeholder={t('companies.forms.edit.placeholders.vat_code', 'VAT code')}
                                        />
                                        <InputError message={errors.vat} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="category">{t('companies.forms.edit.labels.category', 'Category')}</Label>
                                        <MultiSelect
                                            options={companyCategoryOptions}
                                            selected={data.category}
                                            onChange={(selected: string[]) => {
                                                clearErrors('category');
                                                setData('category', selected);
                                            }}
                                            placeholder={t('companies.forms.edit.placeholders.select_categories', 'Select categories...')}
                                            searchPlaceholder={t('companies.forms.edit.placeholders.search_categories', 'Search categories...')}
                                            emptyText={t('companies.forms.edit.placeholders.no_categories', 'No categories found.')}
                                            className={cn(errors.category && invalidFieldClassName)}
                                        />
                                        <InputError message={errors.category} />
                                    </div>
                                </div>

                                <div className="border-primary/20 bg-primary/5 flex items-start gap-2 rounded-sm border p-3 text-sm">
                                    <Info className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                                    <p className="text-foreground">
                                        {t(
                                            'companies.forms.edit.hints_long.identification_readonly',
                                            'Company name and code are managed by an administrator and cannot be edited here.',
                                        )}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        2
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4" />
                                            {t('companies.forms.add.sections_long.address', 'Address')}
                                        </CardTitle>
                                        <CardDescription>
                                            {t('companies.forms.add.sections_long.address_desc', 'Company location or registered address.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="address">{t('companies.forms.edit.labels.address', 'Address')}</Label>
                                    <GoogleMapsProvider>
                                        <div
                                            className={cn(
                                                errors.address && '[&_input]:border-destructive [&_input]:ring-destructive/20 [&_input]:ring-[3px]',
                                            )}
                                        >
                                            <AddressSearchInput
                                                value={
                                                    data.address ||
                                                    [data.address_street, data.address_house_number, data.address_city].filter(Boolean).join(' ')
                                                }
                                                onChange={(val) => {
                                                    clearErrors('address');
                                                    setData('address', val);
                                                }}
                                                onAddressSelect={(addressData) => {
                                                    clearErrors('address');
                                                    setData('address', addressData.label ?? '');
                                                    setData('address_street', addressData.street ?? null);
                                                    setData('address_house_number', addressData.house_number ?? null);
                                                    setData('address_postal_code', addressData.postal_code ?? null);
                                                    setData('address_city', addressData.city ?? null);
                                                    setData('address_municipality', null);
                                                    setData('address_country', addressData.country ?? null);
                                                    setData('address_lat', addressData.lat ?? null);
                                                    setData('address_lng', addressData.lng ?? null);

                                                    const resolvedCountry = resolveCountryFromAddress(addressData.country_code, addressData.country);
                                                    if (resolvedCountry) {
                                                        clearErrors('country');
                                                        setData('country', resolvedCountry);
                                                    }
                                                }}
                                                placeholder={t('companies.forms.edit.placeholders.enter_address', 'Enter address')}
                                                className="w-full"
                                            />
                                        </div>
                                    </GoogleMapsProvider>
                                    <InputError message={errors.address} />
                                </div>

                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-muted-foreground">{t('companies.forms.edit.labels.country', 'Country')}:</span>
                                    <span className="text-foreground font-medium">{countryLabel}</span>
                                </div>
                                <InputError message={errors.country} />

                                <div className="border-primary/25 bg-primary/5 flex items-start gap-2 rounded-sm border p-3 text-sm">
                                    <Info className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                                    <p className="text-foreground">
                                        {t('companies.forms.add.hints.country_auto_from_address', 'Country is auto-detected from selected address.')}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-md">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        3
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle className="flex items-center gap-2">
                                            <Phone className="h-4 w-4" />
                                            {t('companies.forms.add.sections_long.contact', 'Contact information')}
                                        </CardTitle>
                                        <CardDescription>
                                            {t(
                                                'companies.forms.add.sections_long.contact_desc',
                                                'How customers and colleagues will reach this company.',
                                            )}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid items-start gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="flex items-center gap-1.5">
                                            <Phone className="text-muted-foreground h-3.5 w-3.5" />
                                            {t('companies.forms.edit.labels.phone', 'Phone')}
                                        </Label>
                                        <Input
                                            id="phone"
                                            value={data.phone}
                                            aria-invalid={!!errors.phone}
                                            className={cn(errors.phone && invalidFieldClassName)}
                                            onChange={(e) => {
                                                clearErrors('phone');
                                                setData('phone', e.target.value);
                                            }}
                                            autoComplete="tel"
                                            placeholder={t('companies.forms.edit.placeholders.phone_number', 'Phone number')}
                                        />
                                        <InputError message={errors.phone} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="flex items-center gap-1.5">
                                            <Mail className="text-muted-foreground h-3.5 w-3.5" />
                                            {t('companies.forms.edit.labels.email', 'Email')}
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={data.email}
                                            aria-invalid={!!errors.email}
                                            className={cn(errors.email && invalidFieldClassName)}
                                            onChange={(e) => {
                                                clearErrors('email');
                                                setData('email', e.target.value);
                                            }}
                                            autoComplete="email"
                                            placeholder={t('companies.forms.edit.placeholders.company_email', 'Company email')}
                                        />
                                        <InputError message={errors.email} />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="domain">{t('companies.forms.edit.labels.website', 'Website')}</Label>
                                    <Input
                                        id="domain"
                                        value={data.domain}
                                        aria-invalid={!!errors.domain}
                                        className={cn(errors.domain && invalidFieldClassName)}
                                        onChange={(e) => {
                                            clearErrors('domain');
                                            setData('domain', e.target.value);
                                        }}
                                        autoComplete="url"
                                        placeholder={t('companies.forms.edit.placeholders.domain', 'Domain')}
                                    />
                                    <InputError message={errors.domain} />
                                </div>
                            </CardContent>
                        </Card>
                    </ResponsiveModalBody>

                    {/* The form's own sticky bar is gone: the sheet footer pins it. */}
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                            {t('companies.forms.edit.actions.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing
                                ? t('companies.forms.edit.buttons.saving', 'Saving...')
                                : t('companies.forms.edit.actions.save', 'Save changes')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
